import { FC, useContext, useEffect, useRef, useState } from "react";
import NotificationDispatch, { showErrorNotification } from "../../../../../context/notificationContext";
import { useSearchContext } from "../../../../../context/searchContext";
import { useDebounce } from "../../../../../hooks/useDebounce";
import { useEntityAccesses } from "../../../../../hooks/useEntityAccesses";
import { useFilterSearch } from "../../../../../hooks/useFilterSearch";
import useListFilters from "../../../../../hooks/useListFilters";
import useI18n from "../../../../../hooks/useTranslations";
import { HrailTool } from "../../../../../http/hrail/types/tools";
import { useHrailToolsApi } from "../../../../../http/hrail/useTools";
import { AdminItem } from "../../../../../http/types/items";
import { PaginationQuery } from "../../../../../types";
import FlexContent from "../../../../ui/FlexContent";
import InlineOrderEditor from "../../../../ui/InlineOrderEditor";
import { getDeleteAction, getEditAction } from "../../../../ui/ListItemMenu";
import ModalDialog from "../../../../ui/ModalDialog";
import SearchBar from "../../../../ui/SearchBar";
import Table from "../../../../ui/Table";
import Visibility from "../../../../ui/Visibility";
import { VisibilityScopeValue } from "../../../../ui/VisibilityScope";
import CreateToolModalDialogButton from "./CreateToolModalDialogButton";
import ToolForm from "./ToolForm";

type Props = {
  active?: boolean;
};

const Tools: FC<Props> = ({ active }) => {
  const i18n = useI18n();
  const dispatch = useContext(NotificationDispatch);
  const filters = useListFilters({ key: "tools" });
  const aborter = useRef(new AbortController());
  const toolApi = useHrailToolsApi();
  const [itemToEdit, setItemToEdit] = useState<HrailTool>();
  const [itemToDelete, setItemToDelete] = useState<HrailTool>();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { setActions, setTitle } = useSearchContext();
  const { searchTerm, handleSearchChange } = useFilterSearch(filters, aborter, "tools");
  const isActiveTab = active ?? true;
  const updateVisibility = async (tool: HrailTool, visibility: VisibilityScopeValue) => {
    try {
      await toolApi.updateAdminHrailToolVisibility(tool.item.id, { visibility });
    } catch (err) {
      dispatch(showErrorNotification(i18n, err));
    }
  };

  const updateOrder = async (tool: HrailTool, newOrder: number) => {
    try {
      await toolApi.updateAdminHrailToolOrder(tool.item.id, { order: newOrder });
    } catch (err) {
      dispatch(showErrorNotification(i18n, err));
    }
  };

  const { accessesAction, accessesDialog } = useEntityAccesses<AdminItem>("hrail.tool");

  const debouncedValue = useDebounce(searchTerm);

  const query: PaginationQuery = {
    ...filters.state,
    q: debouncedValue,
  };

  const { error, data } = toolApi.useAdminHrailTools(query, aborter.current.signal);

  useEffect(() => {
    if (!isActiveTab) return;

    setTitle(i18n.translation.tools.plural);
    setActions(
      <>
        <SearchBar placeholder={i18n.translation.tools.search} value={searchTerm} onChange={handleSearchChange} />
        <CreateToolModalDialogButton />
      </>,
    );

    return () => {
      setTitle(undefined);
      setActions(null);
    };
  }, [isActiveTab, searchTerm]);

  return (
    <FlexContent padding>
      <Table<HrailTool>
        isTall={true}
        filters={filters}
        head={
          <tr>
            <th align="left">{i18n.translation.items.singular}</th>
            <th align="left">{i18n.translation.common.description}</th>
            <th>{i18n.translation.common.visibility}</th>
            <th>{i18n.translation.common.order}</th>
            <th></th>
          </tr>
        }
        items={data?.items}
        renderRow={(tool) => (
          <>
            <Table.Td noWrap={true}>{tool.item?.code ?? ""}</Table.Td>
            <Table.Td noWrap={false}>{tool.item?.description ?? ""}</Table.Td>
            <td align="center">
              <Visibility value={tool.visibility} onChange={(value) => updateVisibility(tool, value)} />
            </td>
            <td align="center">
              <InlineOrderEditor order={tool.order} onUpdate={(newOrder) => updateOrder(tool, newOrder)} />
            </td>
          </>
        )}
        onRowClick={(tool) => setItemToEdit(tool)}
        rowActions={(tool) => [
          getEditAction(i18n, () => setItemToEdit(tool)),
          accessesAction(tool.item),
          getDeleteAction(i18n, () => setItemToDelete(tool), true),
        ]}
        total={data?.total}
        error={!!error}
      />
      {accessesDialog}

      {!!itemToEdit && (
        <ModalDialog onClose={() => setItemToEdit(undefined)} title={i18n.translation.tools.edit}>
          <ToolForm tool={itemToEdit} onClose={() => setItemToEdit(undefined)} />
        </ModalDialog>
      )}

      {!!itemToDelete && (
        <ModalDialog
          onClose={() => setItemToDelete(undefined)}
          title={i18n.translation.tools.delete}
          isSubmitting={isSubmitting}
          actions={[
            {
              title: i18n.translation.common.cancel,
              onClick: () => setItemToDelete(undefined),
              type: "secondary",
            },
            {
              title: i18n.translation.common.yes,
              onClick: () => {
                setIsSubmitting(true);
                toolApi
                  .deleteAdminHrailTool(itemToDelete.item.id)
                  .then(() => setItemToDelete(undefined))
                  .catch((err) => dispatch(showErrorNotification(i18n, err)))
                  .finally(() => setIsSubmitting(false));
              },
            },
          ]}
        >
          {i18n.translation.common.deleteEntityQuestion(
            i18n.translation.tools.deleteLabel,
            [itemToDelete.item?.code ?? "", itemToDelete.item?.description ?? ""].filter(Boolean).join(" - ") ||
              itemToDelete.item.id,
          )}
        </ModalDialog>
      )}
    </FlexContent>
  );
};

export default Tools;
